import { createRoute, useLoaderData, useRouterState } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { storeFinderPageQueryOptions } from "~/app/api/pageData/storeFinderPageQuery";
import type {
  StoreFinderCompetenceData,
  StoreFinderPageInitialData,
} from "~/app/api/pageData/storeFinderPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createSalesPartnerPage, type ConfigurationData } from "~/features/salesPartner/components/SalesPartnerPage";

const STORE_FINDER_PATH = "/store-finder";

/**
 * Transient router state shape for the share-linear-configuration flow. Both legacy
 * {@code submitAsForm} callers ({@code SavedConfigurationsView}, {@code useRequestOfferPopup})
 * now TanStack-navigate here with {@code shareLinearConfigurationData} carrying the pre-selected
 * configuration codes. See {@link routerHistoryState.d.ts}.
 */
interface StoreFinderRouteState {
  shareLinearConfigurationData?: string[];
}

function toCompetences(competences: StoreFinderCompetenceData[] | null | undefined) {
  if (!competences) {
    return [];
  }
  return competences.map((competence) => ({ key: competence.key, label: competence.label }));
}

/**
 * Merges the page-data body (competence lists) with the transient router state
 * ({@code shareLinearConfigurationData}). Reproduces the retired storefront page adapter
 * transform verbatim.
 *
 * Critical difference from the 0154 transient confirmation pages: a direct URL / refresh to
 * {@code /store-finder} (missing transient state) renders the PLAIN finder — it is a durable
 * page reachable from header / bottom-nav — NOT a redirect. Missing state just means an empty
 * {@code shareLinearConfigurationData}.
 */
function toStoreFinderProps(
  data: StoreFinderPageInitialData | undefined,
  shareLinearConfigurationData: string[],
): ConfigurationData {
  const selected = data?.selectedProductCompetences;
  return {
    shareLinearConfigurationData,
    productCompetences: toCompetences(data?.productCompetences),
    certifiedCompetences: toCompetences(data?.certifiedCompetences),
    selectedProductCompetences: selected && selected.length > 0 ? selected : [],
    googleMapsApiKey: data?.googleMapsApiKey,
  };
}

export { toStoreFinderProps };

function StoreFinderRoute() {
  const result = useLoaderData({
    from: STORE_FINDER_PATH,
  }) as PageInitialData<StoreFinderPageInitialData>;
  const shellProps = useStorefrontShellProps();
  // Transient state from the share-linear-configuration navigate; absent on direct URL / refresh
  // (plain finder render — NOT a redirect, unlike the 0154 confirmation pages).
  const transientState = useRouterState({
    select: (s) => s.location.state,
  }) as StoreFinderRouteState | undefined;
  const shareLinearConfigurationData = transientState?.shareLinearConfigurationData ?? [];

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createSalesPartnerPage(toStoreFinderProps(result.data, shareLinearConfigurationData))}
    </StorefrontShellFrame>
  );
}

export async function loadStoreFinderPageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(storeFinderPageQueryOptions());

  return unwrapPageInitialData(result);
}

export const storeFinderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: STORE_FINDER_PATH,
  loader: async ({ context }) => loadStoreFinderPageData(context),
  component: StoreFinderRoute,
});
